/**
 * TemplateCard
 *
 * Displays a template preview with hover actions (download, save, open in
 * Canva). Supports video templates via `VideoPlayer` and mail front/back.
 */
import React, { useState, useEffect } from 'react';
import { Star, Download, Crown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import { useTemplate } from '../../../api/content/templateActionsApi';
import VideoPlayer from './VideoPlayer';

const TemplateCard = ({ template, onTemplateClick, onSaveTemplate, onImageLoaded }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isImagePreloaded, setIsImagePreloaded] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Use the isSaved property from the template object
  const isSaved = template.isSaved || false;
  // Check if template is obfuscated
  const isObfuscated = template.obfuscated || false;
  // Check if template is a video - make case-insensitive
  const isVideo = (template.type?.toLowerCase() === 'video') && template.videoUrl;

  // Check if template is a mail template
  const isMailTemplate = template.type?.toLowerCase() === 'mail';
  


  // Log the video template information
  useEffect(() => {
    if (isVideo) {
      console.log('Video template:', template.id, template.videoUrl);
    }
  }, [isVideo, template.id, template.videoUrl]);

  // Get user's plan information
  const userPlan = user?.subscriptionStatus?.currentPlanLevel || user?.planLevel || 'basic';
  const isStandardUser = userPlan === 'basic';

  // Preload images (front image and back image for mail templates)
  useEffect(() => {
    const frontImageUrl = isMailTemplate ? 
      (template.frontImageUrl || template.previewImageURL || template.imageUrl) : 
      (template.previewImageURL || template.imageUrl || template.frontImageUrl);
    const backImageUrl = isMailTemplate ? template.backImageUrl : null;
    
    let loadedCount = 0;
    const totalImages = backImageUrl ? 2 : 1;
    
    const handleImageLoad = () => {
      loadedCount++;
      if (loadedCount >= totalImages) {
        setIsImagePreloaded(true);
      }
      };
    
    const handleImageError = () => {
        setImageError(true);
        setIsImagePreloaded(true);
      };
    
    if (frontImageUrl) {
      const frontImg = new Image();
      frontImg.src = frontImageUrl;
      frontImg.onload = handleImageLoad;
      frontImg.onerror = handleImageError;
    }
    
    // Preload back image for mail templates
    if (backImageUrl) {
      const backImg = new Image();
      backImg.src = backImageUrl;
      backImg.onload = handleImageLoad;
      backImg.onerror = handleImageError;
    }
  }, [template.previewImageURL, template.imageUrl, template.frontImageUrl, template.backImageUrl, isMailTemplate]);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };
  
  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleSaveClick = (e) => {
    e.stopPropagation();
    // Use the provided callback if available
    if (onSaveTemplate && !isObfuscated) {
      onSaveTemplate();
    }
  };

  const handleDownload = async (e) => {
    e.stopPropagation();
    
    if (isObfuscated) return;
    
    console.log('Download template:', template.id);
    
    // For mail templates, download both front and back images
    if (isMailTemplate && template.frontImageUrl && template.backImageUrl) {
      // Download front image
      const frontImageName = `${template.title || template.company || 'mail-template'}-front.png`;
      downloadImage(template.frontImageUrl, frontImageName);
      
      // Small delay to avoid overwhelming the browser
      setTimeout(() => {
        // Download back image
        const backImageName = `${template.title || template.company || 'mail-template'}-back.png`;
        downloadImage(template.backImageUrl, backImageName);
      }, 500);
    } 
    // For other templates or mail templates without back image
    else {
      const imageUrl = template.previewImageURL || template.imageUrl || template.frontImageUrl;
      if (imageUrl) {
        const imageName = `${template.title || template.company || 'template'}.png`;
        downloadImage(imageUrl, imageName);
      }
    }
  };

  // Helper function to download an image
  const downloadImage = async (imageUrl, fileName) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading image:', error);
      // Fallback to opening in new tab
      window.open(imageUrl, '_blank');
    }
  };

  const handleCanvaClick = (e) => {
    e.stopPropagation();
    
    if (isObfuscated) return;
    
    console.log('Open in Canva:', template.id);
    
    // For standard users, redirect to pricing page
    if (isStandardUser) {
      navigate('/app/account', { state: { fromTemplates: true } });
      return;
    }
    
    // Track usage when clicking on Canva link
    useTemplate(template.id).catch(err => {
      // Silently log error (non-critical operation)
      console.error('Failed to record template usage:', err);
    });
    
    // For premium users, open Canva link if available, otherwise open landing page
    if (template.canvaLink) {
      window.open(template.canvaLink, '_blank');
    } else if (template.landingPageURL) {
      window.open(template.landingPageURL, '_blank');
    }
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
    // Notify parent that image is loaded
    if (onImageLoaded) {
      onImageLoaded(template.id);
    }
  };

  const handleImageError = () => {
    console.error(`Failed to load image for template: ${template.id}`);
    setImageError(true);
    setImageLoaded(true); // Still consider it loaded so the card is visible
    // Notify parent even if there was an error loading the image
    if (onImageLoaded) {
      onImageLoaded(template.id);
    }
  };
  
  const handleGoPro = (e) => {
    e.stopPropagation();
    navigate('/app/account');
  };
  
  // Force image loading check after a timeout to prevent hanging cards
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!imageLoaded) {
        console.log(`Timeout loading image for template: ${template.id}`);
        setImageLoaded(true);
        // Notify parent after timeout
        if (onImageLoaded) {
          onImageLoaded(template.id);
        }
      }
    }, 5000); // Reduced timeout to 5 seconds
    
    return () => clearTimeout(timer);
  }, [template.id, imageLoaded, onImageLoaded]);

  return (
    <div 
      className={`group relative border border-transparent hover:z-10 transition-all duration-200 ease-in-out ${!isObfuscated ? 'cursor-pointer' : ''}`}
      onClick={() => !isObfuscated && onTemplateClick(template)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Image container */}
      <div className="relative overflow-hidden rounded-sm">
        {/* Skeleton loader shown while image is loading */}
        {!isImagePreloaded && (
          <div className="bg-gray-200 dark:bg-gray-700 animate-pulse w-full aspect-[4/3]">
            <div className="w-full h-full flex items-center justify-center">
              <svg className="w-12 h-12 text-gray-300 dark:text-gray-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-1 16H6c-.55 0-1-.45-1-1V6c0-.55.45-1 1-1h12c.55 0 1 .45 1 1v12c0 .55-.45 1-1 1zm-4.44-6.19l-2.35 3.02-1.56-1.88c-.2-.25-.58-.24-.78.01l-1.74 2.23c-.26.33-.02.81.39.81h8.98c.41 0 .65-.47.4-.8l-2.55-3.39c-.19-.26-.59-.26-.79 0z"/>
              </svg>
            </div>
          </div>
        )}
        
        {/* Video for video templates - using our VideoPlayer component */}
        {isVideo && (
          <VideoPlayer 
            videoUrl={template.videoUrl}
            templateId={template.id}
            isHovered={isHovered}
            className={`absolute inset-0 z-10 ${isHovered ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            style={{ transition: 'opacity 0.3s ease-in-out' }}
          />
        )}
        
        {/* Actual image */}
        {!imageError ? (
          <div 
            className={`relative ${isImagePreloaded ? 'opacity-100' : 'opacity-0'} ${isHovered && isVideo ? 'opacity-0' : 'opacity-100'}`} 
            style={{ 
              transition: 'opacity 0.3s ease-in-out',
              visibility: isImagePreloaded ? 'visible' : 'hidden' 
            }}
          >
            {/* For Mail Templates: Show front by default, back on hover */}
            {isMailTemplate ? (
              <div className="relative w-full">
                {/* Show front or back image based on hover state */}
                {!isHovered ? (
                  /* Front Image */
                  <img 
                    src={template.frontImageUrl || 'https://via.placeholder.com/300x200'} 
                    alt={`${template.title || template.company} - Front`}
                    className={`w-full h-auto display-block transition-opacity duration-300 ${isObfuscated ? 'blur-md' : ''}`}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                  />
                ) : (
                  /* Back Image (only if different from front) */
                  template.backImageUrl && template.backImageUrl !== template.frontImageUrl ? (
                    <img 
                      src={template.backImageUrl} 
                      alt={`${template.title || template.company} - Back`}
                      className={`w-full h-auto display-block transition-opacity duration-300 ${isObfuscated ? 'blur-md' : ''}`}
                      onLoad={handleImageLoad}
                      onError={handleImageError}
                    />
                  ) : (
                    /* Fallback to front image if no back image */
                    <img 
                      src={template.frontImageUrl || 'https://via.placeholder.com/300x200'} 
                      alt={`${template.title || template.company} - Front`}
                      className={`w-full h-auto display-block transition-opacity duration-300 ${isObfuscated ? 'blur-md' : ''}`}
                      onLoad={handleImageLoad}
                      onError={handleImageError}
                    />
                  )
                )}
                

              </div>
            ) : (
              /* For Non-Mail Templates: Standard image display */
              <img 
                src={template.previewImageURL || template.imageUrl || template.frontImageUrl || 'https://via.placeholder.com/300x200'} 
              alt={template.title || template.company}
                className={`w-full h-auto display-block transition-opacity duration-300 ${isObfuscated ? 'blur-md' : ''}`}
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
            )}
            
            {isObfuscated && (
              <div className="absolute inset-0 bg-black/40"></div>
            )}
          </div>
        ) : (
          <div className="bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-center p-4 aspect-[4/3]">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {template.title || "Template image unavailable"}
            </p>
          </div>
        )}
        

        
        {/* Overlay gradient that only appears on hover - with higher z-index */}
        {isHovered && (
          <div 
            className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/10 z-20 animate-fadeIn"
            style={{ animation: 'fadeIn 0.2s ease-out' }}
          ></div>
        )}
        
        {/* Canva icon in the top left - only for non-obfuscated templates */}
        {!isObfuscated && isHovered && (
          <button 
            onClick={handleCanvaClick}
            className="absolute top-2 left-2 sm:top-3 sm:left-3 z-30 animate-fadeIn"
            style={{ animation: 'fadeIn 0.2s ease-out' }}
          >
            <div className="h-8 w-8 sm:h-10 sm:w-10 flex items-center justify-center hover:scale-110 transition-transform duration-150">
              <img 
                src="/images/canva.png" 
                alt="Open in Canva" 
                className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
              />
            </div>
          </button>
        )}
        
        {/* Action buttons container in top right - only for non-obfuscated templates */}
        {!isObfuscated ? (
          <div className={`absolute top-2 right-2 sm:top-4 sm:right-4 flex space-x-2 sm:space-x-3 z-30 ${!isHovered && !isSaved ? 'hidden' : ''}`}>
            {/* Download button */}
            {isHovered && (
              <button 
                onClick={handleDownload}
                className="p-2 sm:p-2.5 rounded-full bg-white shadow-sm hover:shadow-md transition-all duration-150 hover:scale-110 animate-fadeIn"
                style={{ animation: 'fadeIn 0.2s ease-out' }}
                aria-label="Download template"
              >
                <Download className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
              </button>
            )}
            
            {/* Save/Star button - Only show if onSaveTemplate is provided */}
            {onSaveTemplate && (isHovered || isSaved) && (
              <button 
                onClick={handleSaveClick}
                className={`p-2 sm:p-2.5 rounded-full shadow-sm hover:shadow-md transition-all duration-150 hover:scale-110 ${
                  isSaved ? 'bg-gradient-to-r from-purple-500 to-purple-600' : 'bg-white'
                } ${!isHovered && !isSaved ? 'animate-fadeIn' : ''}`}
                style={!isHovered && isSaved ? {} : { animation: 'fadeIn 0.2s ease-out' }}
                aria-label={isSaved ? "Unsave template" : "Save template"}
              >
                <Star 
                  className={`w-4 h-4 sm:w-5 sm:h-5 ${isSaved ? 'text-white fill-white' : 'text-gray-700 fill-none'}`} 
                />
              </button>
            )}
          </div>
        ) : (
          /* Go Premium button for obfuscated templates */
          isHovered && (
            <div className="absolute inset-0 flex items-center justify-center z-30 animate-fadeIn" 
                 style={{ animation: 'fadeIn 0.2s ease-out' }}>
              <button
                onClick={handleGoPro}
                className="bg-gradient-to-r from-purple-500 to-purple-600 text-white font-medium py-2.5 px-5 rounded-md transition-all duration-150 hover:scale-105 shadow-lg flex items-center space-x-2"
              >
                <Crown className="h-4 w-4 text-white" />
                <span>Go Premium</span>
              </button>
            </div>
          )
        )}
        
      </div>
    </div>
  );
};

export default TemplateCard; 